import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { FlexDirectionProps, PositionProps } from 'styled-system';
import { position } from 'styled-system';

import type {
  FlexGapProps,
  ThemeBottomProps,
  ThemeLeftProps,
  ThemeTopProps,
} from '../../theme/system-utilities';
import { flexGap, themeBottom, themeLeft, themeTop } from '../../theme/system-utilities';
import type { ArticleLabelsList } from '../../types/labels';
import type { ProductCampaign } from '../../types/product-card';
import { Flex } from '../Flex';
import { OverlayLabelKind, useOverlayLabels } from './hooks';
import LabelContent from './LabelContent';
import { SponsoredLabel } from './SponsoredLabel';

export type DiscountLabelCampaign = Nullable<
  Pick<ProductCampaign, 'label'> & {
    campaignBanner?: Nullable<
      Pick<ProductCampaign['campaignBanner'], 'legalDisclaimerContent' | 'extraDisclaimerContent'>
    >;
  }
>;

export interface DiscountLabelsProps
  extends ThemeTopProps, ThemeLeftProps, ThemeBottomProps, FlexDirectionProps, FlexGapProps {
  labels_v2: ArticleLabelsList;
  className?: string;
  brand?: string;
  campaigns?: DiscountLabelCampaign[];
  isPLP?: boolean;
  hideHome24Tipp?: boolean;
  withBackButton?: boolean;
  maxItems?: number;
  sponsored?: Nullable<{ owner?: Nullable<string> }>;
  sortLongestBelow?: boolean;
  isSmallVariant?: boolean;
}

const LabelsContainer = styled(Flex)<FlexGapProps & PositionProps & ThemeLeftProps>`
  ${position};
  ${themeTop};
  ${themeBottom};
  ${themeLeft};
  ${flexGap};
`;

const labelContainerStyles: ComponentProps<typeof LabelsContainer> = {
  position: 'absolute',
  alignItems: 'flex-start',
};

const bottomContainerStyles: ComponentProps<typeof LabelsContainer> = {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 'sp_4',
};

const DiscountLabels = ({
  labels_v2,
  themeTop: themeTopProp = ['sp_8', 'sp_16', null, 'sp_24'],
  themeBottom: themeBottomProp,
  themeLeft: themeLeftProp = ['sp_8', 'sp_20', null, 'sp_32'],
  gap = 'sp_8',
  flexDirection = 'column',
  className,
  brand,
  campaigns,
  isPLP,
  hideHome24Tipp,
  withBackButton,
  maxItems = 2,
  sponsored,
  sortLongestBelow = false,
  isSmallVariant = false,
}: DiscountLabelsProps) => {
  const labels = useOverlayLabels({
    labels_v2,
    sponsored,
    hideHome24Tipp,
    maxItems,
    sortLongestBelow,
  });

  if (!labels.length) return null;

  return (
    <LabelsContainer
      {...labelContainerStyles}
      data-testid="labels-container"
      className={className}
      flexDirection={flexDirection}
      themeLeft={themeLeftProp}
      gap={gap}
      {...(themeTopProp && !themeBottomProp ? { themeTop: themeTopProp } : {})}
      {...(themeBottomProp
        ? {
            themeBottom: themeBottomProp,
            ...bottomContainerStyles,
          }
        : {})}
    >
      {labels.map((item, index) =>
        item.kind === OverlayLabelKind.Sponsored ? (
          <SponsoredLabel key="overlay-sponsored" owner={item.owner} />
        ) : (
          <LabelContent
            key={`label-${item.label.type}-${index}`}
            type={item.label.type}
            campaigns={campaigns}
            value={item.label.value}
            index={index}
            themeBottom={themeBottomProp}
            isPLP={isPLP}
            withBackButton={withBackButton}
            brand={brand}
            isSmallVariant={isSmallVariant}
          />
        )
      )}
    </LabelsContainer>
  );
};

export default DiscountLabels;
